@use '~/theme/theme' as *;
@use '~/theme/typography';

.input {
  display: flex;
  border-radius: 8px;

  @include typography.caption;
  @include typography.medium;
}

.inputWrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: spacing(1);
}

.datePickerField {
  --border-color: #{color(grey-600)};
  --preset-opacity: 1;
  --date-inputs-opacity: 1;

  // Matches the inputWrapper when no date segment has been filled in yet.
  // Wrapped in :where() to give it zero specificity, so any state rule always wins.
  $has-some-value: ':where(:has(:is([role="spinbutton"], [role="textbox"]):not([data-placeholder])))';
  $is-empty: ':not(#{$has-some-value})';

  .inputWrapper {
    padding: spacing(1) spacing(1.5);
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: white;
    width: 100%;

    .presetDate,
    .placeholder {
      position: absolute;
      opacity: var(--preset-opacity);
      pointer-events: none;
    }

    .dateInputs {
      opacity: var(--date-inputs-opacity);
    }

    &.hasPreset {
      --date-inputs-opacity: 0;
      --preset-opacity: 1;

      .dateInputs {
        color: color(grey-600);
      }
    }

    &#{$is-empty} {
      color: color(grey-600);

      --date-inputs-opacity: 0;
    }

    &#{$has-some-value} {
      --preset-opacity: 0;
    }

    &[data-focused] {
      outline: 1px solid var(--border-color);

      --border-color: #{color(violet-600)};
      --preset-opacity: 0;
      --date-inputs-opacity: 1;
    }

    &[data-invalid] {
      --border-color: #{color(lobster-800)};
    }

    &[data-hovered] {
      --border-color: #{color(violet-600)};
      --preset-opacity: 0;
      --date-inputs-opacity: 1;
    }

    &[data-focus-visible] {
      --preset-opacity: 0;
      --date-inputs-opacity: 1;
    }

    .input {
      &[slot='start'] {
        padding-right: spacing(0.5);
      }

      &[slot='end'] {
        padding-left: spacing(0.5);
      }
    }

    .icon {
      flex-shrink: 0;
    }
  }

  &[data-open] .inputWrapper {
    --border-color: #{color(violet-600)};
    --preset-opacity: 0;
    --date-inputs-opacity: 1;

    outline: 1px solid var(--border-color);
  }

  &[data-disabled] {
    .inputWrapper {
      background-color: color(grey-100);
      color: color(grey-500);

      --border-color: #{color(grey-400)};
    }

    .input {
      background-color: transparent;
      color: color(grey-500);
    }
  }

  .calendarButton,
  .clear {
    padding: 0;
    height: var(--action-icon-size);
    width: var(--action-icon-size);
    border-radius: 6px;
  }
}

.datePickerField[data-size='small'] {
  --action-icon-size: 24px;

  .inputWrapper {
    height: 32px;
  }

  .input {
    ::placeholder {
      @include typography.caption;
      @include typography.medium;

      color: color(grey-600);
    }
  }
}

.datePickerField[data-size='medium'] {
  --action-icon-size: 24px;

  .inputWrapper {
    height: 38px;
  }

  .input {
    @include typography.body2;
    @include typography.regular;

    &::placeholder {
      @include typography.body2;
      @include typography.regular;

      color: color(grey-600);
    }
  }
}

.datePickerField[data-size='large'] {
  --action-icon-size: 32px;

  .inputWrapper {
    height: 38px;
  }

  .input {
    @include typography.body1;
    @include typography.regular;

    ::placeholder {
      @include typography.body1;
      @include typography.regular;

      color: color(grey-600);
    }
  }
}

.datePickerField .inputWrapper:has(.input[data-focus-within]) {
  --preset-opacity: 0;
  --date-inputs-opacity: 1;
}

.segment {
  &[data-focus-visible],
  &[data-focused] {
    outline: 2px solid color(violet-600);
    outline-offset: 1px;
    border-radius: 2px;
  }
}

.popover {
  overflow: auto;

  .dialog {
    display: grid;

    &:has(.presetDates) {
      grid-template-columns: 220px 1fr;
    }
  }

  .presetDates {
    height: 100%;
    padding: spacing(3) spacing(1.5);
    border-right: border(grey-300);
    min-height: 0;
  }

  .presetTitle {
    color: color(violet-600);
    display: flex;
    justify-content: space-between;
    padding: 5px spacing(1.5);
  }

  .iconFlash {
    color: color(orange-700);
    width: 20px;
    height: 20px;
  }

  .presetList {
    flex: 1 1 0;
    overflow-y: auto;

    // Push the scrollbar outside the padding area
    margin-right: spacing(-1.5);
    padding-right: spacing(1.5);
  }

  .preset {
    font-family: 'Public Sans', sans-serif;
    padding: spacing(1.5);
    background: white;
    border: none;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &[data-focus-visible] {
      outline: 2px solid color(violet-600);
      border-radius: 4px;
    }

    &:hover {
      background: color(violet-100);
    }
  }

  .calendar {
    display: flex;
    flex-direction: row;

    // Always render with the same height regardless of the number of rows.
    // This helps with the UX as the popover does not "jump" around when
    // cycling through months with more/less weeks.
    min-height: 528px;
  }

  .detail {
    color: color(grey-600);
  }

  .gridWrapper {
    padding: spacing(3);

    .grid {
      border-collapse: collapse;
    }

    & + .gridWrapper {
      border-left: border(grey-300);
    }
  }

  .header {
    display: grid;
    align-items: center;

    > * {
      grid-area: 1 / 1;
    }

    .heading {
      justify-self: center;
      margin: 0;
    }

    [slot='previous'] {
      justify-self: start;
    }

    [slot='next'] {
      justify-self: end;
    }
  }

  .heading {
    @include typography.body1;
    @include typography.semibold;
  }

  .headerCell {
    @include typography.body2;
    @include typography.semibold;

    height: 40px;
    color: color(grey-600);
  }

  [role='gridcell'] {
    padding-top: spacing(2);
  }

  .calendarCell {
    @include typography.body2;
    @include typography.regular;

    transition: background 300ms;
    color: color(grey-800);
    border-radius: 50%;
    display: flex;
    width: 48px;
    height: 48px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: auto;

    &[data-focus-visible],
    &[data-focused] {
      outline: 2px solid color(violet-600);
      outline-offset: 2px;
    }

    &[data-outside-month] {
      color: color(grey-500);
    }

    &[data-unavailable] {
      color: color(grey-500);
      cursor: not-allowed;
    }

    &[data-hovered]:not([data-unavailable]) {
      background: color(violet-100);
    }

    &[data-selected]:not([data-outside-month]) {
      background: color(violet-600);
      color: white;
      border-radius: 0;

      &[data-selection-start] {
        border-top-left-radius: 50%;
        border-bottom-left-radius: 50%;
      }

      &[data-selection-end] {
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;
      }
    }
  }

  .today {
    background: color(violet-200);
  }

  .disabled {
    color: color(grey-500);
    cursor: default;
  }
}
